import { Button } from "@portal/BaseComponents/Button/AntButton";
import { Flex } from "@portal/BaseComponents/Flex/Flex";
import { HideWrapper } from "@portal/BaseComponents/HideWrapper/HideWrapper";
import { ImageComponent } from "@portal/BaseComponents/Image/AntImage";
import { ToolTip } from "@portal/BaseComponents/ToolTip/AntTooltip";
import { TypographyText } from "@portal/BaseComponents/Typography/AntTypography";
import { getAttachmentUrl } from "@portal/api/download";
import { Icon } from "@portal/lib/icons/icons";
import { FileType, Files } from "@portal/types/file";
import { getDocTypeIcon } from "@portal/util/docIcon";
import {
  bytesToSize,
  getFileExtensionFromName,
  getFileTypeFromExtension,
  removeFileExtension,
} from "@portal/util/file";
import { Spin, UploadFile } from "antd";
import { useCallback, useEffect, useState } from "react";
import { DocPreview } from "../DocViewer/Preview/DocPreview";
import { StyledUploadedFiles } from "./style";
export type PreviewType = "UPLOADED" | "EXPIRE_LINK";
export type PreviewMode = "BUTTON" | "UPLOADED_FILE" | "HIDE";
interface UploadedFilesProps {
  file: Files;
  previewIcon?: React.ReactNode;
  onRemove?: (id: string) => void;
  closable?: boolean;
  previewType: PreviewType;
  fileObject?: UploadFile<Files>["originFileObj"];
  previewMode?: PreviewMode;
  hide?: boolean;
  preview?: boolean;
  handleOpenChange?: (open: boolean) => void;
  onSuccess?: () => void;
  deleteButton?: (fileId: string, fileName: string) => JSX.Element
}

const CloseIcon = <Icon icon={["far", "times"]} color="var(--gray-7)" />;

function UploadedFiles(props: UploadedFilesProps) {
  const {
    file,
    previewIcon,
    onRemove,
    closable = true,
    previewType,
    fileObject,
    previewMode = "UPLOADED_FILE",
    hide = false,
    preview = false,
    handleOpenChange,
    onSuccess,
  } = props;
  const [previewUrl, setPreviewUrl] = useState<string>();
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileExtension = getFileExtensionFromName(file.file_name);
  const fileType = fileExtension
    ? getFileTypeFromExtension(fileExtension)
    : FileType.PDF;
  const isImageDoc = fileType === FileType.JPG || fileType === FileType.PNG;
  const FileIcon = previewIcon
    ? previewIcon
    : getDocTypeIcon(fileType as FileType);
  const FileName = removeFileExtension(file.file_name ?? "");
  const previewCondition = isImageDoc ? false : showPreview;

  function handleClose() {
    onRemove?.(file.id);
  }
  async function handlePreview() {
    try {
      setLoading(true);
      const url = await getAttachmentUrl(file.id);
      setPreviewUrl(url.expiry_link);
      setShowPreview(true);
      setLoading(false);
      onSuccess?.();
    } catch (e) {
      throw new Error("error in fetching invoice");
    }
  }

  const previewCallBack = useCallback(handlePreview, [file.id, onSuccess]);
  function handleUploadedFilesPreview() {
    if (previewType === "EXPIRE_LINK") return;
    setPreviewUrl(URL.createObjectURL(fileObject as unknown as Blob));
    setLoading(false);
    setShowPreview(true);
  }

  useEffect(() => {
    if (previewType === "EXPIRE_LINK") return;
    return () => URL.revokeObjectURL(previewUrl as string);
  }, [previewType, previewUrl]);

  useEffect(() => {
    if (preview) {
      previewCallBack();
    }
  }, [preview, previewCallBack]);
  const PreviewSection = (
    <DocPreview
      docType={fileType}
      docUrl={previewUrl}
      onClose={() => {
        setShowPreview(false);
        handleOpenChange?.(false);
      }}
      open={previewCondition}
    />
  );

  const Fileinfo = (
    <Flex align="center">
      {FileIcon}
      <TypographyText
        style={{ maxWidth: "120px" }}
        ellipsis={{ tooltip: FileName }}
      >
        {FileName}
      </TypographyText>
      {props.deleteButton?.(file.id, file.file_name)}
    </Flex>
  );

  const Content = (
    <Flex
      onClick={
        previewType === "UPLOADED" ? handleUploadedFilesPreview : handlePreview
      }
      style={{ cursor: "pointer" }}
      space="2"
      vertical
    >
      {Fileinfo}
      <TypographyText
        fs="12"
        style={{ color: "var(--gray-7)", paddingInlineStart: "18px" }}
      >
        {bytesToSize(file.file_size)}
      </TypographyText>
      
    </Flex>
  );

  const ImagePreview = (
    <div
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <ImageComponent
        style={{ display: "none" }}
        src={previewUrl}
        preview={{
          visible: isImageDoc && showPreview,
          src: previewUrl,
          zIndex: 13_000,
          onVisibleChange: (value) => {
            setShowPreview(value);
            if (!value) {
              handleOpenChange?.(false);
            }
          },
        }}
      />
    </div>
  );

  const UploadedPreview = (
    <StyledUploadedFiles
      closable={loading ? false : closable}
      onClose={(e) => {
        e.stopPropagation();
        handleClose();
      }}
      closeIcon={CloseIcon}
    >
      {Content}
      {loading && <Spin size="small" />}
    </StyledUploadedFiles>
  );

  const Preview = (
    <ToolTip title="Preview">
      <Button
        icon={previewIcon}
        onClick={handlePreview}
        size="middle"
        loading={loading}
      />
    </ToolTip>
  );

  return (
    <HideWrapper hide={hide}>
      {previewMode === "BUTTON"
        ? Preview
        : previewMode === "UPLOADED_FILE"
        ? UploadedPreview
        : previewMode === "HIDE"
        ? null
        : UploadedPreview}
      {isImageDoc && ImagePreview}
      {PreviewSection}
    </HideWrapper>
  );
}

export { UploadedFiles };
